import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DeleteModal from 'components/Modals/DeleteModal';
import useLoads from 'hooks/useLoads';
import SpinnerModal from 'components/SpinnerModal';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { YieldStrings } from '../../../constants';

const DeleteConfirmation = ({ id, open, onCancel, onResult }) => {
  const {
    deleteLoad,
    updateTasksStatus,
    getTasksStatus,
    hasActiveTasks
  } = useLoads();
  const amplitude = useContext(AmplitudeContext);
  const [isDeleting, setIsDeleting] = useState(false);

  // Update hasActiveTasks flag when modal opens
  useEffect(() => {
    if (open) {
      const selectedCropSeasons = JSON.parse(
        localStorage.getItem('selectedCropSeasons')
      );
      if (selectedCropSeasons?.length) {
        getTasksStatus({ seasonIds: selectedCropSeasons });
      }
    }
  }, [open, getTasksStatus]);

  // Helper function to update task status with tracking ID
  const updateTaskStatus = async (trackingId, context = 'success') => {
    if (!trackingId) return undefined;

    const selectedCropSeasons = JSON.parse(
      localStorage.getItem('selectedCropSeasons')
    );
    const updateResult = await updateTasksStatus(
      trackingId,
      selectedCropSeasons
    );
    return updateResult;
  };

  const handleDelete = async () => {
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.YieldV2.loads.deleteYield
    );
    // Set local loading state and close the delete modal
    setIsDeleting(true);
    onCancel(false);
    try {
      const { trackingId } = await deleteLoad({ id });

      // Update task status for successful delete
      const updateResult = await updateTaskStatus(trackingId, 'success');
      if (trackingId && !updateResult?.success) {
        throw new Error('Task status update failed');
      }

      onResult({
        type: 'success',
        title: 'Load deleted.',
        message: '',
        showToast: true,
        showRequestList: hasActiveTasks
      });
    } catch (error) {
      // Extract tracking ID from error response headers if available
      const errorTrackingId =
        error.response?.headers?.['x-request-tracking-id'];

      // Only update task status for non-504 errors
      // 504 timeout means the request might still be processing
      if (errorTrackingId && error.response?.status !== 504) {
        await updateTaskStatus(errorTrackingId, 'error');
      }

      if (error.response?.status === 504) {
        onResult({
          type: 'warning',
          title: YieldStrings.toast.timeoutWarningTitle,
          message: YieldStrings.toast.timeoutWarningContent,
          showToast: true,
          showRequestList: hasActiveTasks
        });
      } else {
        onResult({
          type: 'error',
          title: 'Unable to delete load.',
          message:
            'An error prevented your load from being deleted. Please try again.',
          showToast: true,
          showRequestList: hasActiveTasks
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if (!open && !isDeleting) return null;
  if (isDeleting) return <SpinnerModal open message="Deleting Load..." />;
  return (
    <DeleteModal
      open={open}
      type="error"
      itemType="Load"
      onCancel={onCancel}
      onDelete={handleDelete}
    />
  );
};

DeleteConfirmation.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onResult: PropTypes.func.isRequired
};

export default DeleteConfirmation;
